import { useEffect, useContext, useState, FC } from 'react'

import { VisualizationKeyString } from 'types'
import { useVisualizationType } from 'hooks'
import { Alert, AlertFilter } from 'AlertsOfVulnerability/types'
import readAlerts from 'AlertsOfVulnerability/services/calls/alerts/readAlerts'
import { ALERT_INITIAL_FILTER, ALERT_INITIAL_PAGINATION } from 'AlertsOfVulnerability/constants/pages/alerts'
import { useAlertsTypesForFilterManager } from 'AlertsOfVulnerability/contexts/AlertsTypesForFilterManager/Provider'
import { useSetMetadataAndResetMetadataInfos } from 'components/filters/MetadatasFilterTemplate/hooks'
import AlertsManagerContext from './Context'
import { parserCitizenFilter } from 'modules/CitizenProfile/utils/parserCitizenFilter'
import getValidAlertType from 'modules/AlertsOfVulnerability/services/calls/alerts/alertsTypes/getValidAlertType'

type Props = {
  children: JSX.Element
}

const AlertsManagerProvider: FC<Props> = ({ children }) => {
  const [loadingAlerts, setLoadingAlerts] = useState(true)
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [filter, setFilter] = useState<AlertFilter>(ALERT_INITIAL_FILTER)
  const [visualization, setVisualization] = useState<VisualizationKeyString>({})
  const [totalItems, setTotalItems] = useState(0)
  const [pagination, setPagination] = useState(ALERT_INITIAL_PAGINATION)

  const { alertsTypes, setSelectAlertType } = useAlertsTypesForFilterManager()

  const { setVisualizationType, visualizationType, visualizationTypeStorageKey } = useVisualizationType(
    'alerts-of-vulnerability/alerts/visualization-type'
  )

  useSetMetadataAndResetMetadataInfos({
    filterFieldName: 'case_type_name',
    filter,
    setFilter,
    metadatas: alertsTypes,
    setMetadata: setSelectAlertType,
  })

  async function getAlertTypeForFilter() {
    const [selectedAlertType] = alertsTypes.filter((alertType) => {
      if (filter.case_type_id) return alertType.id === filter.case_type_id[0]
      return false
    })

    const response = await getValidAlertType({ alertTypeSlug: selectedAlertType.slug })
    if (response?.header?.success) {
      const alertTypeFromAPI = response?.body?.case_type
      setSelectAlertType(alertTypeFromAPI || {})
    }
  }

  function setFilterAndResetPagination(newFilter: AlertFilter) {
    setFilter({ ...filter, ...newFilter })
    setPagination({ ...pagination, page: 1 })
  }

  const getAlerts = async () => {
    const filters = {
      filter: parserCitizenFilter({ filter }),
      pagination,
    }
    const response = await readAlerts({ filters })

    if (response?.header?.success) {
      const { elements, totalElements } = response?.body?.case
      setAlerts(elements)
      setTotalItems(totalElements)
    }
  }

  async function reloadAlerts() {
    setLoadingAlerts(true)
    await getAlerts()
    setLoadingAlerts(false)
  }

  useEffect(() => {
    if (filter.case_type_id && filter.case_type_id.length > 0) {
      getAlertTypeForFilter()
    }
  }, [filter])

  useEffect(() => {
    reloadAlerts()
  }, [filter, pagination])

  return (
    <AlertsManagerContext.Provider
      value={{
        filter,
        loadingAlerts,
        visualization,
        setVisualization,
        visualizationType,
        setVisualizationType,
        visualizationTypeStorageKey,
        setFilterAndResetPagination,
        totalItems,
        setTotalItems,
        pagination,
        setPagination,
        alerts,
        setAlerts,
        reloadAlerts,
      }}
    >
      {children}
    </AlertsManagerContext.Provider>
  )
}

export const useAlertsManager = () => useContext(AlertsManagerContext)

export default AlertsManagerProvider
